import { useState, useEffect, useCallback, useRef } from 'react';
import type { StorageAdapter, FormDraftType, FormDraftEnvelope } from '../storage/types';
import { createDraftKey, createDraftEnvelope, parseAndMigrateDraft } from '../storage/draftParser';

export type DraftSaveStatus = 'idle' | 'saving' | 'saved_locally' | 'error';

export interface UseFormDraftOptions<T extends object> {
  storage: StorageAdapter;
  formType: FormDraftType;
  userId?: string | null;
  entityId: string | null;
  targetStatus?: string | null;
  initialValues: T;
  debounceMs?: number;
  ttlMs?: number;
  onRestore?: (restoredData: T, envelope: FormDraftEnvelope<FormDraftType, T>) => void;
}

export function useFormDraft<T extends object>({
  storage,
  formType,
  userId,
  entityId,
  targetStatus,
  initialValues,
  debounceMs = 400,
  ttlMs = 7 * 24 * 60 * 60 * 1000,
  onRestore,
}: UseFormDraftOptions<T>) {
  const [values, setValues] = useState<T>(initialValues);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasRestoredDraft, setHasRestoredDraft] = useState(false);
  const [restoredAt, setRestoredAt] = useState<string | null>(null);
  const [draftStatus, setDraftStatus] = useState<DraftSaveStatus>('idle');

  const valuesRef = useRef(values);
  valuesRef.current = values;

  const initialValuesRef = useRef(initialValues);
  initialValuesRef.current = initialValues;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSaveRef = useRef(false);
  const pendingWriteRef = useRef<(() => void) | null>(null);
  const onRestoreRef = useRef(onRestore);
  onRestoreRef.current = onRestore;

  // Which key the current `values` belong to, so we can tell a real entity switch
  // from the key simply resolving once userId / entityId finish loading.
  const loadedKeyRef = useRef<string | null>(null);

  const storageKey =
    userId && entityId
      ? createDraftKey(formType, userId, entityId, targetStatus)
      : null;

  // Flush any pending debounced write when the component unmounts
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (pendingWriteRef.current) {
        pendingWriteRef.current();
        pendingWriteRef.current = null;
      }
    };
  }, []);

  // 1. Hydrate saved draft on mount or key change
  useEffect(() => {
    if (!storageKey || !userId || !entityId) {
      pendingSaveRef.current = false;
      pendingWriteRef.current = null;
      loadedKeyRef.current = null;
      setValues(initialValuesRef.current);
      setIsLoaded(true);
      setHasRestoredDraft(false);
      setRestoredAt(null);
      return;
    }

    let active = true;

    // Moving between two entities: reset synchronously. Without this the previous
    // entity's answers stay in `values` when we switch to an entity that has no
    // stored draft — and the next keystroke persists them under the new key.
    // A key that was simply null until userId / entityId resolved is not a switch,
    // so anything typed before it resolved is left alone.
    if (loadedKeyRef.current !== null && loadedKeyRef.current !== storageKey) {
      pendingSaveRef.current = false;
      pendingWriteRef.current = null;
      setValues(initialValuesRef.current);
      setHasRestoredDraft(false);
      setRestoredAt(null);
      setDraftStatus('idle');
    }
    loadedKeyRef.current = storageKey;
    setIsLoaded(false);

    (async () => {
      try {
        const stored = await storage.getItem(storageKey);
        if (stored && active) {
          const envelope = parseAndMigrateDraft<FormDraftEnvelope<FormDraftType, T>>(
            stored,
            formType,
            {
              userId,
              entityId,
              storageKey,
              targetStatus,
              ttlMs,
            }
          );

          if (envelope && envelope.data && typeof envelope.data === 'object') {
            const restored = { ...initialValuesRef.current, ...envelope.data } as T;
            setValues(restored);
            setHasRestoredDraft(true);
            setRestoredAt(envelope.updatedAt || envelope.createdAt || new Date().toISOString());
            setDraftStatus('saved_locally');
            if (onRestoreRef.current) {
              onRestoreRef.current(restored, envelope);
            }
          } else {
            // Invalid or expired: purge from storage
            await storage.removeItem(storageKey).catch(() => {});
          }
        }
      } catch (err) {
        console.warn('[useFormDraft] Hydration error:', err);
        await storage.removeItem(storageKey).catch(() => {});
      } finally {
        if (active) setIsLoaded(true);
      }
    })();

    return () => {
      active = false;
    };
  }, [storage, storageKey, formType, userId, entityId, targetStatus, ttlMs]);

  // 2. Perform write to storage
  const performSave = useCallback(
    async (dataToSave: T) => {
      if (!storageKey || !userId || !entityId) return;
      try {
        setDraftStatus('saving');
        const envelope = createDraftEnvelope(
          formType,
          storageKey,
          userId,
          entityId,
          dataToSave,
          targetStatus
        );
        await storage.setItem(storageKey, JSON.stringify(envelope));
        setDraftStatus('saved_locally');
      } catch (err) {
        console.warn('[useFormDraft] Failed to write draft:', err);
        setDraftStatus('error');
      }
    },
    [storage, storageKey, formType, userId, entityId, targetStatus]
  );

  // 3. Debounced trigger for user typing
  const triggerDebouncedSave = useCallback(
    (newValues: T) => {
      if (!storageKey) return;
      if (timerRef.current) clearTimeout(timerRef.current);

      pendingWriteRef.current = () => performSave(newValues);
      timerRef.current = setTimeout(() => {
        pendingWriteRef.current = null;
        timerRef.current = null;
        performSave(newValues);
      }, debounceMs);
    },
    [storageKey, performSave, debounceMs]
  );

  // 4. Save effect: triggered when user edits via setFieldValue or patchValues
  useEffect(() => {
    if (!pendingSaveRef.current) return;
    pendingSaveRef.current = false;
    triggerDebouncedSave(values);
  }, [values, triggerDebouncedSave]);

  // 5. Update a single field (pure updater, marks pending save)
  const setFieldValue = useCallback(
    <K extends keyof T>(field: K, val: T[K] | ((prevVal: T[K]) => T[K])) => {
      pendingSaveRef.current = true;
      setValues((prev) => {
        const resolvedVal =
          typeof val === 'function'
            ? (val as (prevVal: T[K]) => T[K])(prev[field])
            : val;
        return { ...prev, [field]: resolvedVal };
      });
    },
    []
  );

  // 6. Patch multiple fields (pure updater, marks pending save)
  const patchValues = useCallback(
    (partial: Partial<T> | ((prev: T) => Partial<T>)) => {
      pendingSaveRef.current = true;
      setValues((prev) => {
        const resolved = typeof partial === 'function' ? partial(prev) : partial;
        return { ...prev, ...resolved };
      });
    },
    []
  );

  // 7. Immediate synchronous flush (e.g. on backgrounding or discrete clicks)
  const saveNow = useCallback(
    async (overrideValues?: T) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      pendingWriteRef.current = null;
      pendingSaveRef.current = false;
      const dataToSave = overrideValues ?? valuesRef.current;
      await performSave(dataToSave);
    },
    [performSave]
  );

  // 8. Clear draft (on form submit or discard)
  const clearDraft = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    pendingWriteRef.current = null;
    pendingSaveRef.current = false;
    setHasRestoredDraft(false);
    setRestoredAt(null);
    setDraftStatus('idle');
    if (storageKey) {
      await storage.removeItem(storageKey).catch(() => {});
    }
  }, [storage, storageKey]);

  // 9. Reset form back to initial values and purge draft
  const resetForm = useCallback(async () => {
    await clearDraft();
    setValues(initialValuesRef.current);
  }, [clearDraft]);

  return {
    values,
    setValues,
    setFieldValue,
    patchValues,
    saveNow,
    clearDraft,
    resetForm,
    isLoaded,
    hasRestoredDraft,
    restoredAt,
    draftStatus,
  };
}
